import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function TelephonieAgenceImmobiliere() {
  return (
    <IntentLandingLayout
      canonical="/telephonie-agence-immobiliere"
      title="Téléphonie pour agence immobilière : VoIP, renvoi et IA"
      description="Guide de téléphonie pour agence immobilière : numéro principal, renvoi d'appel, VoIP, débordement et couche IA pour mieux traiter les appels entrants."
      eyebrow="Téléphonie agence immobilière"
      h1="Téléphonie pour agence immobilière : numéro, renvoi, VoIP et IA"
      lede="Une bonne architecture téléphonique doit permettre à l'agence de rester joignable sans imposer aux négociateurs de répondre à chaque appel au milieu d'une visite."
      serviceType="Solution de téléphonie pour agence immobilière"
      primaryCtaLabel="Ajouter une couche IA"
      definition={<>La <strong>téléphonie d'une agence immobilière</strong> combine généralement un numéro public, une ou plusieurs lignes ou extensions, des règles de routage et éventuellement un standard VoIP. Une couche IA peut s'ajouter à cette architecture pour traiter les appels qui ne sont pas décrochés.</>}
      sections={[
        {
          title: "Les briques d'une téléphonie d'agence",
          bullets: [
            "Le numéro principal communiqué sur le site, les portails et les supports de l'agence.",
            "Les lignes ou extensions utilisées par les collaborateurs.",
            "Le standard ou système VoIP qui distribue les appels.",
            "Les règles de renvoi en cas de non-réponse, fermeture ou indisponibilité.",
            "La messagerie, la permanence humaine ou l'assistant IA utilisé comme solution de secours.",
          ],
        },
        {
          title: "Le renvoi d'appel est la clé pour ajouter l'IA sans tout remplacer",
          paragraphs: [
            "Dans la plupart des organisations, le besoin n'est pas de changer de numéro mais de décider ce qui doit se produire lorsqu'un appel n'est pas pris en charge.",
            "Un renvoi conditionnel peut envoyer l'appel vers Immovox après quelques sonneries. Un renvoi horaire peut couvrir uniquement les périodes de fermeture. Cette logique permet de conserver le système existant tout en ajoutant une continuité de traitement.",
          ],
        },
        {
          title: "VoIP, standard cloud ou ligne classique : que faut-il vérifier ?",
          bullets: [
            "La possibilité de configurer des renvois conditionnels ou horaires.",
            "Le comportement du numéro affiché et de l'identification de l'appelant.",
            "La façon dont les appels simultanés sont gérés.",
            "Les règles de transfert vers les collaborateurs.",
            "La facilité à modifier l'organisation sans intervention lourde de l'opérateur.",
          ],
        },
        {
          title: "Quel rôle pour un standard téléphonique IA ?",
          intro: "L'IA n'a pas besoin de devenir votre système téléphonique principal.",
          bullets: [
            "Prendre le relais lorsque personne ne décroche.",
            "Identifier le motif avant de solliciter un collaborateur.",
            "Collecter les informations utiles avant un rappel.",
            "Traiter différemment les demandes transaction, location et gestion.",
            "Fournir un historique et une synthèse homogène des appels pris en charge.",
          ],
        },
        {
          title: "Checklist avant de modifier la téléphonie d'une agence",
          bullets: [
            "Lister les numéros réellement publiés et utilisés.",
            "Cartographier les horaires et situations où les appels sont perdus.",
            "Définir qui doit être joignable pour les demandes prioritaires.",
            "Tester les renvois et les transferts avant la mise en production.",
            "Prévoir une solution de repli simple si le nouveau scénario doit être désactivé.",
          ],
        },
      ]}
      comparison={{
        title: "Quelle brique répond à quel besoin ?",
        headers: ["Besoin", "Brique téléphonique", "Rôle"],
        rows: [
          ["Distribuer les appels", "Standard / VoIP", "Faire sonner les bonnes lignes"],
          ["Couvrir une non-réponse", "Renvoi d'appel", "Envoyer l'appel vers un autre point de traitement"],
          ["Prendre un message", "Messagerie", "Enregistrer la demande sans interaction structurée"],
          ["Externaliser l'accueil", "Permanence humaine", "Faire répondre un opérateur"],
          ["Qualifier automatiquement", "Standard IA", "Comprendre la demande et préparer sa reprise"],
        ],
      }}
      faq={[
        { question: "Faut-il passer en VoIP pour utiliser Immovox ?", answer: "Pas nécessairement. Le point essentiel est de pouvoir renvoyer les appels vers le numéro de prise en charge Immovox." },
        { question: "Peut-on garder le numéro principal de l'agence ?", answer: "Oui. Le renvoi d'appel permet de conserver le numéro public existant." },
        { question: "Quelle différence entre un standard cloud et un standard IA ?", answer: "Un standard cloud organise principalement la téléphonie et le routage. Un standard IA ajoute une conversation capable d'identifier et qualifier la demande." },
        { question: "Le renvoi peut-il être limité aux appels non décrochés ?", answer: "Oui, lorsque votre opérateur ou système téléphonique propose un renvoi conditionnel sur non-réponse." },
        { question: "Peut-on transférer ensuite vers un collaborateur ?", answer: "Des scénarios de transfert peuvent être prévus selon la configuration et le type de demande." },
      ]}
      related={[
        { to: "/standard-telephonique-immobilier", label: "Standard téléphonique immobilier", description: "La couche de traitement et de qualification des appels." },
        { to: "/permanence-telephonique-immobilier", label: "Permanence téléphonique", description: "Le relais lorsque l'équipe n'est pas disponible." },
        { to: "/accueil-telephonique-immobilier", label: "Accueil téléphonique", description: "La qualité du premier échange avec l'appelant." },
      ]}
      ctaTitle="Ajouter Immovox à votre téléphonie actuelle"
      ctaText="Gardez votre numéro et votre organisation. Testez d'abord le renvoi d'appel et les scénarios sur les situations où vous perdez aujourd'hui des appels."
    />
  );
}
